import React, { useState, useEffect } from "react";
import { Check } from "lucide-react";
import { API_URLS } from "../api/urls";
import { ReactComponent as Download } from "../../assets/icons/svg/download.svg";
import { ReactComponent as GiftBox } from "../../assets/icons/svg/gift-box.svg";
import PartyConfetti from "../../assets/icons/gif/Party Confetti.gif";

interface PaymentSuccessPageProps {
  userId: number | null;
  planType: "lite" | "spark" | "ignite" | "thrive" | "premium";
  selectedDuration: number; // in months
  paymentDetails: {
    orderId: string;
    paymentId: string;
    amount: number;
    transactionDate: string;
    invoiceNumber?: string;
    invoicePdf?: string;
    userPaymentId?: number;
  };
  onNavigateHome: () => void;
  preloadedFeatures?: string[];
}

interface PlanDetail {
  subscription_plan_id: number;
  subscription_plan_name: string;
  std_subscription_perks: string;
  duration_and_price_and_perks: {
    standard_details_id: number;
    std_subscription_price: string;
    std_subscription_discount_price: string;
    duration_period: string;
    product_id: string;
    std_subscription_description: string;
    std_subscription_perfect_for: string;
    std_subscription_not_included: string;
  }[];
}

interface ApiResponse {
  success: string;
  message: string;
  plans: PlanDetail[];
}

const PaymentSuccessPage: React.FC<PaymentSuccessPageProps> = ({
  userId,
  planType,
  selectedDuration,
  paymentDetails,
  onNavigateHome,
  preloadedFeatures,
}) => {
  const [planDetails, setPlanDetails] = useState<PlanDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [features, setFeatures] = useState<string[]>(preloadedFeatures ?? []);
  const [isDownloadingInvoice, setIsDownloadingInvoice] = useState(false);

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fetch plan details from API in background (skipped when preloadedFeatures provided)
  useEffect(() => {
    if (preloadedFeatures && preloadedFeatures.length > 0) return;

    const fetchPlanDetails = async () => {
      try {
        // Don't set loading to true - let content show immediately

        const response = await fetch(API_URLS.Get_STANDARD_PLANS, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data: ApiResponse = await response.json();

        if (data.success !== "1") {
          throw new Error(data.message || "Failed to fetch plans");
        }

        // Find the plan that matches the selected planType
        const planMapping: Record<string, string> = {
          lite: "Triva Lite",
          spark: "Triva Spark",
          ignite: "Triva Ignite",
          thrive: "Triva Thrive",
          premium: "Triva Signature",
        };

        const matchedPlan = data.plans?.find((plan: PlanDetail) =>
          plan.subscription_plan_name
            .toLowerCase()
            .includes(planMapping[planType].toLowerCase())
        );

        if (matchedPlan) {
          setPlanDetails(matchedPlan);

          // Parse features from std_subscription_perks
          const parsedFeatures = parsePerksToArray(
            matchedPlan.std_subscription_perks
          );
          setFeatures(parsedFeatures);
        } else {
          setError("Plan details not found");
        }
      } catch (err) {
        console.error("Error fetching plan details:", err);
        setError("Failed to load plan details");
      }
      // No finally block needed - we don't use loading state anymore
    };

    fetchPlanDetails();
  }, [planType, preloadedFeatures]);

  // Helper function to parse perks string into array
  const parsePerksToArray = (perks: string): string[] => {
    if (!perks) return [];

    let cleanedPerks = perks
      .replace(/<[^>]*>/g, "") // Remove HTML tags
      .replace(/\\r\\n|\\r|\\n|\r\n|\r|\n/g, "|BREAK|") // Replace line breaks
      .trim();

    if (!cleanedPerks.includes("|BREAK|")) {
      return cleanedPerks ? [cleanedPerks] : [];
    }

    return cleanedPerks
      .split("|BREAK|")
      .map((perk) => perk.trim())
      .filter((perk) => perk && perk.length > 0)
      .map((perk) => perk.replace(/,$/, "")); // Remove trailing commas
  };

  // Get plan name for display
  const getPlanDisplayName = () => {
    if (planDetails?.subscription_plan_name) {
      // Extract the main name (before parentheses if any)
      const planNameMatch = planDetails.subscription_plan_name.match(
        /^([^(]+)(?:\s*\(([^)]+)\))?/
      );
      return planNameMatch
        ? planNameMatch[1].trim()
        : planDetails.subscription_plan_name;
    }

    const planNames: Record<string, string> = {
      lite: "Triva Lite",
      spark: "Triva Spark",
      ignite: "Triva Ignite",
      thrive: "Triva Thrive",
      premium: "Triva Signature",
    };

    return planNames[planType] || "Triva Plan";
  };

  // Download backend-generated invoice
  const handleDownloadReceipt = async () => {
    if (!paymentDetails.userPaymentId) {
      console.error("❌ User Payment ID is missing");
      alert("Unable to download invoice. Payment ID is missing.");
      return;
    }

    try {
      setIsDownloadingInvoice(true);
      console.log("=== DOWNLOADING BACKEND INVOICE ===");
      console.log("📋 User Payment ID:", paymentDetails.userPaymentId);
      
      // Get the download URL using the user_payment_id
      const downloadUrl = API_URLS.GET_INVOICE(paymentDetails.userPaymentId);
      
      console.log("🚀 Download URL:", downloadUrl);
      console.log("⏰ Download Request Timestamp:", new Date().toISOString());

      // Fetch the invoice PDF
      const response = await fetch(downloadUrl, {
        method: 'GET',
      });

      console.log("📡 Response Status:", response.status);
      console.log("📡 Response Headers:", Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorText = await response.text();
        console.error("❌ Download HTTP Error Response:", errorText);
        throw new Error(`Failed to download invoice: ${response.status}`);
      }

      // Get the blob from response
      const blob = await response.blob();
      
      console.log("✅ Invoice blob received:", {
        size: blob.size,
        type: blob.type
      });

      // Create a download link and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename
      const filename = `Triva_Invoice_${paymentDetails.invoiceNumber || paymentDetails.userPaymentId}_${new Date().toISOString().split('T')[0]}.pdf`;
      link.download = filename;

      // Append to body, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the URL object
      window.URL.revokeObjectURL(url);

      console.log("✅ Invoice downloaded successfully:", filename);
      console.log("=== DOWNLOAD COMPLETE ===");

    } catch (error) {
      console.error("=== DOWNLOAD INVOICE ERROR ===");
      console.error("❌ Error Type:", error instanceof Error ? error.constructor.name : typeof error);
      console.error("❌ Error Message:", error instanceof Error ? error.message : String(error));
      console.error("❌ Full Error Object:", error);
      console.error("⏰ Error Timestamp:", new Date().toISOString());
      console.error("=== DOWNLOAD INVOICE ERROR END ===");
      
      alert(
        `Failed to download invoice: ${error instanceof Error ? error.message : 'Unknown error'}\n\nPlease try again or contact support.`
      );
    } finally {
      setIsDownloadingInvoice(false);
    }
  };

  return (
    <div className="min-h-screen bg-white px-[24px] mt-[32px]">
      {/* Header with Download Receipt */}
      <div className="flex justify-end py-4">
        <button
          onClick={handleDownloadReceipt}
          disabled={isDownloadingInvoice || !paymentDetails.userPaymentId}
          className="flex flex-col items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isDownloadingInvoice ? (
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#0DB5B5]"></div>
          ) : (
            <Download className="w-6 h-6" />
          )}
          <span className="text-[10px] font-medium font-inter text-[#202020]">
            {isDownloadingInvoice ? 'Downloading...' : 'Download receipt'}
          </span>
        </button>
      </div>

      <div className="pb-8">
        {/* Success Animation and Gift Icon */}
        <div className="text-start mb-6">
          <div className="relative flex items-center justify-center mx-auto -mb-10 w-[300px] h-[300px]">
            {/* Background GIF Animation */}
            <img
              src={PartyConfetti}
              alt="Party Confetti Animation"
              className="absolute inset-0 w-full h-full object-contain z-0"
            />

            {/* Gift Box Icon aligned left-center */}
            <div className="relative z-10 flex items-center justify-center h-full w-full">
              <GiftBox className="w-[160px] h-[160px] translate-x-[-70px]" />
            </div>
          </div>

          <div className="z-10 text-[20px] font-bold font-inter text-[#003E3E] mb-1">
            Thanks for subscribing
          </div>

          <div className="z-10 text-[20px] font-bold text-[#003E3E] font-inter">
            {getPlanDisplayName()} plan!
          </div>
        </div>

        <div className="mb-6">
          <div className="text-[12px] 3xl:text-[16px] text-[#003E3E] font-inter">
            Your plan is being prepared and will be shared with you within
            <span className="font-semibold"> 24 hours (or sooner).</span>
            Thank you for your patience!
          </div>
        </div>
        <div className="mb-3">
          <div className="text-[14px] font-medium text-[#003E3E] font-inter">
            Here's everything you're getting
          </div>
        </div>
        <div className="border-[0.5px] border-[#0DB5B5] rounded-[20px] p-3">
          {/* What You Get Section */}
          {error ? (
            // Error state - show friendly message
            <div className="text-center py-4">
              <p className="text-[14px] text-gray-600 mb-2">
                Loading plan details...
              </p>
              <p className="text-[12px] text-gray-500">
                Your subscription is active and ready to use!
              </p>
            </div>
          ) : features && features.length > 0 ? (
            // Loaded features
            <div className="space-y-2">
              {features.map((feature, index) => (
                <div key={index} className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                    <Check className="w-3 h-3 text-[#0DB5B5]" />
                  </div>
                  <span className="text-[14px] text-[#202020] font-inter leading-relaxed">
                    {feature}
                
                  </span>
                </div>
              ))}
            </div>
          ) : (
            // Empty state (shouldn't normally happen)
            <div className="text-center py-4">
              <p className="text-[14px] text-gray-500">
                Loading plan features...
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-center p-4 mb-6">
        <button
          onClick={onNavigateHome}
          className="bg-[#0DB5B5] text-white px-8 py-3 rounded-xl font-medium text-[16px] transition-colors duration-200 hover:bg-[#0A9999]"
        >
          Okay, got it
        </button>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;